import { reaction } from "mobx";
import { observer } from "mobx-react-lite";
import React from "react";
import { Linking } from "react-native";
import { PlaniraLogType } from "../../../shared/types";
import { ModuleType, PlanQuestionType } from "../../../templates/types";
import { trackEvent } from "../../core/trackEvent";
import type CalendarModuleModel from "../../modules/Calendar/CalendarModuleModel";
import type LocationModuleModel from "../../modules/Location/LocationModuleModel";
import type PlanModel from "../../plans/PlanModel";
import { log } from "../../ui/log";
import { findCheckinAndCheckout } from "../utils";
import type { SearchHotelsParams } from "./apis";
import { HotelsApiManager } from "./HotelsApiManager";
import { HotelsTipsBar } from "./HotelsTipsBar";

export const PlanHotelsTipsUI = observer(({ plan }: { plan: PlanModel }) => {
  const [manager] = React.useState(
    () =>
      new HotelsApiManager((newCache) => {
        plan.updateCache({
          affiliateHotelsDump: newCache,
        });
      }, plan.data.cache.affiliateHotelsDump),
  );

  const modules = plan.modules;

  const locationModule =
    (modules.find(
      (m) => m.data.questionType === PlanQuestionType.Location && m.isDone,
    ) as LocationModuleModel | undefined) ??
    (modules.find((m) => m.data.type === ModuleType.Location && m.isDone) as
      | LocationModuleModel
      | undefined);

  const datesModule =
    (modules.find(
      (m) => m.data.questionType === PlanQuestionType.Dates && m.isDone,
    ) as CalendarModuleModel | undefined) ??
    (modules.find((m) => m.data.type === ModuleType.Calendar && m.isDone) as
      | CalendarModuleModel
      | undefined);

  /*
    console.info("Got data to build on", {
      locationModule: locationModule?.data.selectedLocations,
      datesModule: datesModule?.data.selectedDates,
    });
    */

  React.useEffect(() => {
    // Combined reaction for selected location and selected dates
    const combinedReaction = reaction(
      () => {
        const selectedLocation =
          locationModule?.data.snapshot.selectedLocationsLatLng?.[0];
        const selectedDates = datesModule?.data.selectedDates;
        return { selectedLocation, selectedDates };
      },
      ({ selectedLocation, selectedDates }) => {
        log.dev("Got tips data", { selectedLocation, selectedDates });

        if (selectedLocation) {
          const params: Partial<SearchHotelsParams> = {
            latitude: selectedLocation.lat,
            longitude: selectedLocation.lng,
          };

          if (selectedDates?.length) {
            const dates = findCheckinAndCheckout(selectedDates);

            if (dates.checkin_date && dates.checkout_date) {
              params.checkin_date = dates.checkin_date;
              params.checkout_date = dates.checkout_date;
            }
          }

          manager.updateParams(params);
        }
      },
      { fireImmediately: true },
    );

    // Cleanup reaction when the component unmounts
    return () => {
      combinedReaction();
    };
  }, [manager, locationModule?.data, datesModule?.data]);

  if (!manager.hotels.length) {
    // Hidden for now, kaka på kaka
    return null;

    /* return (
      <Box position="relative" backgroundColor={"$backgroundMutedBlock"}>
        <XStack gap="$space.small" alignItems="center">
          <Lightbulb />
          <Title>{t("plan_hotels_tips_waiting_title")}</Title>
        </XStack>
        <YStack
          gap={"$medium"}
          $xs={{
            paddingRight: 134,
          }}
          paddingRight={163}
        >
          <Body>{t("plan_hotels_tips_waiting_description")}</Body>

          <Image
            $xs={{
              paddingRight: -40,
            }}
            right={-12}
            position="absolute"
            bottom={-29}
            zIndex={1}
            src={require("../../../assets/illustrations/laptopgirl.png")}
          />
        </YStack>
      </Box>
    );*/
  }

  return (
    <HotelsTipsBar
      daysCount={manager.daysCount}
      onSelect={(tip) => {
        if (tip.url) {
          const affiliateUrl = manager.getAffiliateUrl(tip.url);

          trackEvent("affiliate_hotels_click");

          void plan.addPlaniraLogItem(PlaniraLogType.TappedOnTip, affiliateUrl);

          Linking.openURL(affiliateUrl); // Open the external link
        }
      }}
      tips={manager.hotels}
    />
  );
});
